"use client";

import React from "react";
import { Check, Minus, ShieldCheck, Wind, ArrowRight, Info } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const inspectionTypes = [{
  id: "4-point",
  title: "4-Point Inspection",
  description: "A focused evaluation of the four main systems of a home. This is typically requested by insurance companies before binding coverage, especially on older properties.",
  icon: ShieldCheck,
  badges: ["Insurance Requirement", "Systems Focus"],
  focusAreas: ["Roofing System Condition & Age", "Electrical Wiring & Panels", "Plumbing Connections & Fixtures", "HVAC (Heating & Cooling) Systems"]
}, {
  id: "wind-mit",
  title: "Wind Mitigation Inspection",
  description: "may provide potential premium discounts depending on carrier",
  icon: Wind,
  badges: ["Potential Insurance Credits"],
  focusAreas: ["Roof Covering & Shape", "Roof-to-Wall Connections", "Roof Deck Attachment", "Window & Door Protection (Shutters)"]
}];
const comparisonData = [{
  feature: "Primary Purpose",
  fourPoint: "Assess condition of 4 major systems",
  windMit: "Evaluate windstorm resistance features"
}, {
  feature: "Insurance Impact",
  fourPoint: "Often required to obtain or renew coverage",
  windMit: "May support insurance credits depending on the carrier"
}, {
  feature: "Typical Trigger",
  fourPoint: "Home is typically 20+ years old",
  windMit: "Homeowner or carrier requests wind-resistance documentation"
}, {
  feature: "Roof Evaluation",
  fourPoint: "General condition, leaks, & estimated lifespan",
  windMit: "Detailed attachment methods, shape, & coverings"
}, {
  feature: "HVAC, Plumbing, Electrical",
  fourPoint: "Focused visual review",
  windMit: "Not inspected"
}, {
  feature: "Opening Protection (Windows/Doors)",
  fourPoint: "Not inspected",
  windMit: "Detailed evaluation of impact resistance"
}];
export default function FourPointVsWindMitigationInspection() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: cardsRef,
    isVisible: areCardsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: tableRef,
    isVisible: isTableVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: ctaRef,
    isVisible: isCtaVisible
  } = useRevealOnScroll({
    threshold: 0.5
  });
  return <section id="4-point-vs-wind-mitigation-inspection" className="bg-background py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Section Header */}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Badge variant="outline" className="mb-4">
            Service Comparison
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-foreground mb-6 tracking-tight">
            4-Point vs. Wind Mitigation
          </h2>
          <p className="text-lg text-muted-foreground font-sans">
            Understanding the difference between these two specialized inspections can help you secure the right insurance coverage and potentially save money on premiums.
          </p>
        </div>

        {/* Feature Cards */}
        <div ref={cardsRef} className={`grid md:grid-cols-2 gap-8 mb-16 transition-all duration-700 delay-100 ease-out ${areCardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {inspectionTypes.map((type, index) => {
          const Icon = type.icon;
          return <Card key={type.id} data-index={index} className="h-full flex flex-col border-border shadow-sm hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-primary/10 rounded-lg">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex gap-2">
                      {type.badges.map((badge, bIndex) => <Badge key={bIndex} data-index={bIndex} variant="secondary" className="text-xs">
                          {badge}
                        </Badge>)}
                    </div>
                  </div>
                  <CardTitle className="text-2xl font-default">{type.title}</CardTitle>
                  <CardDescription className="text-base mt-2">
                    {type.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-grow">
                  <h4 className="font-semibold text-foreground mb-3 font-default flex items-center gap-2">
                    <Info className="w-4 h-4 text-muted-foreground" />
                    Key Focus Areas:
                  </h4>
                  <ul className="space-y-2">
                    {type.focusAreas.map((area, aIndex) => <li key={aIndex} data-index={aIndex} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                        <span>{area}</span>
                      </li>)}
                  </ul>
                </CardContent>
              </Card>;
        })}
        </div>

        {/* Comparison Table */}
        <div ref={tableRef} className={`mb-16 transition-all duration-700 delay-200 ease-out ${isTableVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold font-default text-foreground">
              Detailed Feature Comparison
            </h3>
            <p className="text-muted-foreground mt-2">
              A side-by-side look at what each inspection covers.
            </p>
          </div>

          <Card className="overflow-hidden border-border shadow-sm">
            <Table>
              <TableHeader className="bg-muted/50">
                <TableRow>
                  <TableHead className="w-[30%] font-semibold text-foreground">Feature</TableHead>
                  <TableHead className="w-[35%] font-semibold text-foreground">4-Point Inspection</TableHead>
                  <TableHead className="w-[35%] font-semibold text-foreground">Wind Mitigation</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {comparisonData.map((row, index) => <TableRow key={index} data-index={index}>
                    <TableCell className="font-medium text-foreground">
                      {row.feature}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {row.fourPoint === "Not inspected" ? <span className="flex items-center gap-2 text-muted-foreground/70">
                          <Minus className="w-4 h-4" /> Not inspected
                        </span> : <span className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-primary shrink-0" /> {row.fourPoint}
                        </span>}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {row.windMit === "Not inspected" ? <span className="flex items-center gap-2 text-muted-foreground/70">
                          <Minus className="w-4 h-4" /> Not inspected
                        </span> : <span className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-primary shrink-0" /> {row.windMit}
                        </span>}
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </Card>
        </div>

        {/* Bottom CTA */}
        <div ref={ctaRef} className={`text-center bg-muted/30 rounded-2xl p-8 md:p-12 border border-border transition-all duration-700 delay-300 ease-out ${isCtaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h3 className="text-2xl md:text-3xl font-bold font-default text-foreground mb-4">
            Still not sure which inspection you need?
          </h3>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Many homeowners have both inspections completed during the same appointment when their insurance carrier requests the documentation. Any available credits are determined by the carrier. Contact us to discuss your specific property needs.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button asChild size="lg" className="font-semibold">
              <Link to="https://trusted-fl.com/#schedule-your-inspection">
                Schedule an Inspection
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="https://trusted-fl.com/#our-inspection-services">
                View All Services
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}